import { useQuery } from '@tanstack/react-query';
import { messageDErreur, useSession } from '../auth/session';
import { maintenant } from '../horloge';
import { Alerte } from '../ui';

interface Occurrence {
  startsAt: string;
  endsAt: string;
  parentSlot: number;
  source: 'schedule' | 'exception';
}

const JOURS = ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'];
const MOIS = [
  'janvier', 'février', 'mars', 'avril', 'mai', 'juin',
  'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre',
];

const dateLisible = (iso: string): string => {
  const d = new Date(iso);
  return `${JOURS[d.getDay()]} ${d.getDate()} ${MOIS[d.getMonth()]}`;
};

/**
 * Aperçu des prochaines périodes de garde.
 *
 * C'est ce que le parent invité voit en arrivant : un calendrier déjà rempli, avant
 * toute démarche. La valeur du produit doit être visible avant qu'on lui demande quoi
 * que ce soit (docs/02 §1.3).
 */
export function ApercuGarde({
  caseFileId,
  monSlot,
  jours = 21,
  depuis = maintenant(),
}: {
  caseFileId: string;
  monSlot: number | null;
  jours?: number;
  /** Début de la fenêtre affichée. Injectable pour rendre l'aperçu déterministe en test. */
  depuis?: Date;
}) {
  const { client } = useSession();
  const jusqu = new Date(depuis.getTime() + jours * 86_400_000);

  const periodes = useQuery({
    queryKey: ['occurrences', caseFileId, jours],
    queryFn: () =>
      client.request<Occurrence[]>(`/case-files/${caseFileId}/custody/occurrences`, {
        query: { from: depuis.toISOString(), to: jusqu.toISOString() },
      }),
  });

  if (periodes.isPending) return <p className="discret">Chargement du calendrier…</p>;

  if (periodes.isError) {
    return <Alerte titre="Le calendrier n’a pas pu être chargé">{messageDErreur(periodes.error)}</Alerte>;
  }

  if (periodes.data.length === 0) {
    return (
      <p className="discret">
        Aucun schéma de garde n'est encore défini pour ce dossier.
      </p>
    );
  }

  return (
    <>
      <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
        {periodes.data.map((periode) => {
          const aMoi = monSlot !== null && periode.parentSlot === monSlot;
          return (
            <li
              key={`${periode.startsAt}-${periode.parentSlot}`}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: 'var(--espace-md)',
                padding: 'var(--espace-sm) 0',
                borderBottom: '1px solid var(--couleur-border)',
              }}
            >
              <span>
                Du {dateLisible(periode.startsAt)} au {dateLisible(periode.endsAt)}
              </span>
              <strong>{aMoi ? 'Chez vous' : "Chez l'autre parent"}</strong>
            </li>
          );
        })}
      </ul>
      <p className="discret" style={{ marginBottom: 0 }}>
        Prochains {jours} jours, d'après le schéma de garde du dossier.
      </p>
    </>
  );
}
